import { useCallback, useState } from 'react';

import { useReactiveVar } from '@apollo/client';

import { useRemoveGroupMutation } from 'core/graphql';
import { popupData, PopupNames } from 'core';

import { GroupsQuery, SettingsMetaQuery } from 'core/operations/queries';

import { useGroupPermissions } from './useGroupPermissions';

type deleteGroupType = [
  () => void,
  {
    success: boolean;
    loading: boolean;
    error?: { message?: string };
    /**
     * Сервер отказал (`success: false`): `notAvailable` — у группы есть
     * договоры или заявки преподавателей (`delete_is_not_available`,
     * перепроверка F27), `failed` — любой другой отказ. Раньше такой ответ
     * не показывался никак: окно оставалось открытым молча.
     */
    refusal: 'notAvailable' | 'failed' | null;
  },
];

const DELETE_NOT_AVAILABLE = 'delete_is_not_available';

export const useDeleteGroup = (): deleteGroupType => {
  const groupData = useReactiveVar(popupData)[PopupNames.deleteGroup];
  // Те же переменные, что у самих списков (поля и счётчики по правам роли).
  const { canReadGroupStaffFields, canReadGroupCounts } = useGroupPermissions();
  const [removeGroupMutation, { data, loading }] = useRemoveGroupMutation({
    refetchQueries: [
      {
        query: GroupsQuery,
        variables: {
          first: 10,
          withStaffFields: canReadGroupStaffFields,
        },
      },
      {
        query: SettingsMetaQuery,
        variables: { withGroups: canReadGroupCounts },
      },
    ],
  });
  const [error, setError] = useState<{ message?: string }>();
  const deleteGroup = useCallback(() => {
    if (groupData && groupData.groupId) {
      removeGroupMutation({
        variables: { groupId: Number(groupData.groupId) },
      }).catch((err) =>
        setError({
          message: err.message,
        }),
      );
    }
  }, [removeGroupMutation, groupData]);

  const payload = data?.deleteGroup;
  let refusal: 'notAvailable' | 'failed' | null = null;
  if (payload && !payload.success) {
    refusal = JSON.stringify(payload.errors ?? '').includes(
      DELETE_NOT_AVAILABLE,
    )
      ? 'notAvailable'
      : 'failed';
  }

  return [
    deleteGroup,
    {
      success: payload?.success || false,
      loading,
      error,
      refusal,
    },
  ];
};
